import { useCallback, useEffect, useState } from "react";

import { AnalyticsFieldName } from "@/constants/analytics/Analytics";
import {
  LoginAnalyticsEvent,
  LoginAnalyticsPageView,
} from "@/constants/analytics/Login";
import { ResetAppText } from "@/constants/Login";
import { LoginScreenPath } from "@/constants/Navigation";
import { LoginTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useClearCache from "@/hooks/useClearCache";
import useHelpCenter from "@/hooks/useHelpCenter";
import useLoginInitialization from "@/hooks/useLoginInitialization";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import LegalFooterLinks, {
  FooterLinksLayout,
} from "@/ui/components/authentication/LegalFooterLinks";
import LoginForm from "@/ui/components/authentication/LoginForm";
import LoginScreenContainer from "@/ui/components/authentication/LoginScreenContainer";
import LoginScreenContent from "@/ui/components/authentication/LoginScreenContent";
import AppVersionLabel from "@/ui/components/common/AppVersionLabel";
import ErrorBoundaryUI from "@/ui/components/errorBoundary/ErrorBoundaryUI";
import ConfirmationAlert from "@/ui/composed/alerts/ConfirmationAlert";
import { Button } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";

const Login = () => {
  const { showToast } = useToast();
  const { track, trackPageView } = useAnalytics();
  const { captureException } = useAppMonitor();
  const { loginUnauthenticatedHelpCenter, presentHelpCenter } = useHelpCenter();
  const { clearCache } = useClearCache();
  const { isInitializationSuccess, isRestartRequired } = useLoginInitialization(
    {
      canLoginInitializationBegin: true,
    },
  );
  const [isHelpCenterLoading, setIsHelpCenterLoading] =
    useState<boolean>(false);
  const [isClearCacheModalOpen, setIsClearCacheModalOpen] =
    useState<boolean>(false);

  const handleResetPassword = useCallback(() => {
    track(LoginAnalyticsEvent.ForgotPasswordPressed, {
      [AnalyticsFieldName.Source]: LoginAnalyticsPageView.Login,
    });
    router.navigate(LoginScreenPath.ForgotPassword);
  }, [track]);

  const handleClearCachePress = () => {
    track(LoginAnalyticsEvent.ClearCache);
    setIsClearCacheModalOpen(true);
  };

  const handleClearCacheConfirm = () => {
    track(LoginAnalyticsEvent.ClearCacheConfirmed);
    setIsClearCacheModalOpen(false);
    void clearCache();
  };

  const handleClearCacheCancel = () => {
    setIsClearCacheModalOpen(false);
  };

  const handleHelpCenterPressed = useCallback(async () => {
    track(LoginAnalyticsEvent.ContactHelpCenterPressed, {
      [AnalyticsFieldName.Source]: LoginAnalyticsPageView.Login,
    });

    if (isHelpCenterLoading) {
      return;
    }
    try {
      setIsHelpCenterLoading(true);

      await loginUnauthenticatedHelpCenter();
      await presentHelpCenter({
        intercomPresenterSource: LoginAnalyticsPageView.Login,
        isSecured: false,
      });
    } catch (error) {
      captureException(error);
      showToast({
        text: "There was an error while trying to contact support. Please try again later",
        variant: "error",
      });
    } finally {
      setIsHelpCenterLoading(false);
    }
  }, [
    captureException,
    isHelpCenterLoading,
    loginUnauthenticatedHelpCenter,
    presentHelpCenter,
    showToast,
    track,
  ]);

  useEffect(() => {
    trackPageView(LoginAnalyticsPageView.Login);
  }, [trackPageView]);

  if (isRestartRequired) {
    return <ErrorBoundaryUI />;
  }

  return (
    <LoginScreenContainer testID={LoginTestID.Login}>
      <LoginScreenContent
        subtitle="Please sign in below to access your account"
        testID={LoginTestID.Login}
        title="Welcome to Equip!"
      >
        <LoginForm isLoginDisabled={!isInitializationSuccess} />
      </LoginScreenContent>

      <HStack gap={18} justifyContent="center">
        <Button
          accessibilityLabel="Forgot password?"
          onPress={handleResetPassword}
          size="small"
          testID={LoginTestID.ForgotPassword}
          title="Forgot password?"
          variant="secondary"
        />
        <Button
          accessibilityLabel="Contact support"
          isLoading={isHelpCenterLoading || !isInitializationSuccess}
          onPress={handleHelpCenterPressed}
          size="small"
          testID={LoginTestID.HelpCenter}
          title="Contact support"
          variant="secondary"
        />
      </HStack>
      <VStack alignItems="center" gap={8}>
        <HStack alignSelf="center" gap={FooterLinksLayout.spacing}>
          <LegalFooterLinks analyticsSource={LoginAnalyticsPageView.Login} />
          <Button
            accessibilityLabel={ResetAppText.Trigger}
            onPress={handleClearCachePress}
            size="small"
            testID={LoginTestID.ClearCache}
            title={ResetAppText.Trigger}
            variant="tertiary"
          />
          <ConfirmationAlert
            confirmationType="destructive"
            confirmText={ResetAppText.Trigger}
            isOpen={isClearCacheModalOpen}
            message={ResetAppText.ConfirmationMessage}
            onConfirm={handleClearCacheConfirm}
            onDismiss={handleClearCacheCancel}
            testID={LoginTestID.ClearCacheConfirmation}
            title={ResetAppText.ConfirmationTitle}
          />
        </HStack>
        <AppVersionLabel testID={LoginTestID.Login} />
      </VStack>
    </LoginScreenContainer>
  );
};

export default Login;
